import { FormControl, FormLabel, Grid, MenuItem, OutlinedInput, Tooltip, Typography } from "@mui/material";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { ConfirmDialog } from "../../Components/Core/ConfirmDialog";
import { ControlPointDuplicate, Info } from "@mui/icons-material";
import { AddressLookup, ClearableSelect, DatePicker, SiteSelect } from "../../Components/CommonInputs";
import { CloneQuoteDto, RegionDto, SiteDto, QuoteDto } from "../../dtos";
import { FormInput, FormNumberInput } from "../../Components/CoreLib/library";
import { IEntityAutocomplete } from "../../Components/CommonInputs/IEntityAutocomplete";
import { useGetRegionQuery } from "../../store/generated/generatedApi";
import { countries, countriesByCode, fromEasternWallDate, toEasternWallDate } from "../../util";
import { StateAutocomplete } from "../../Components/CommonInputs/StateAutocomplete";

export interface ICloneQuoteDialog {
    onClose: () => void;
    onConfirm: (
        cloneQuoteDto: CloneQuoteDto
    ) => void;
    open: boolean;
    quoteEffectiveOn: Date;
    quoteExpirationDate?: Date;
    isCopying?: boolean;
    quote?: QuoteDto;
}

export const CloneQuoteDialog: FC<ICloneQuoteDialog> = (props) => {
    const { onClose, onConfirm, open, quoteEffectiveOn, quoteExpirationDate, isCopying, quote } = props;
    const [effectiveOn, setEffectiveOn] = useState<Date | null | undefined>(quoteEffectiveOn ?? null);
    const [expirationDate, setExpirationDate] = useState<Date | null | undefined>(quoteExpirationDate ?? null);
    const [priceIncreasePercentage, setPriceIncreasePercentage] = useState<number>();
    const [jobName, setJobName] = useState<string>(quote?.jobName ?? '');
    const [addressLine1, setAddressLine1] = useState<string>(quote?.addressLine1 ?? '');
    const [addressLine2, setAddressLine2] = useState<string>(quote?.addressLine2 ?? '');
    const [city, setCity] = useState<string>(quote?.city ?? '');
    const [state, setState] = useState<string>(quote?.state ?? 'IN');
    const [zipCode, setZipCode] = useState<string>(quote?.zipCode ?? '');
    const [country, setCountry] = useState<string>(quote?.country ?? countriesByCode.US);
    const [site, setSite] = useState<SiteDto | undefined>(undefined);
    const [siteName, setSiteName] = useState<string>(quote?.siteName ?? '');
    const [region, setRegion] = useState<RegionDto | undefined>(quote?.region ?? undefined);
    const [miles, setMiles] = useState<number | undefined>(quote?.miles ?? undefined);
    const [minutes, setMinutes] = useState<number | undefined>(quote?.minutes ?? undefined);
    const [toOrFromJob, setToOrFromJob] = useState<string>(quote?.toOrFromJob ?? '');

    const { currentData: regions } = useGetRegionQuery({ searchText: '', sorting: ['NAME+'], page: 0, pageSize: 100000, includeInactive: false });

    useEffect(() => {
        setExpirationDate(quoteExpirationDate ?? null);
        setJobName(quote?.jobName ?? '');
        setAddressLine1(quote?.addressLine1 ?? '');
        setAddressLine2(quote?.addressLine2 ?? '');
        setCity(quote?.city ?? '');
        setState(quote?.state ?? 'IN');
        setZipCode(quote?.zipCode ?? '');
        setCountry(quote?.country ?? countriesByCode.US);
        setSiteName(quote?.siteName ?? '');
        setRegion(quote?.region ?? undefined);
        setMiles(quote?.miles ?? undefined);
        setMinutes(quote?.minutes ?? undefined);
        setToOrFromJob(quote?.toOrFromJob ?? '');
    }, [quote, quoteExpirationDate]);
    
    const handleOnConfirm = useCallback(() => {
        if (effectiveOn && !isNaN(Date.parse(effectiveOn.toString())) && expirationDate && !isNaN(Date.parse(expirationDate.toString()))) {
            onConfirm({
                effectiveOn,
                expirationDate,
                priceIncreasePercentage,
                jobName,
                addressLine1,
                addressLine2,
                city,
                state,
                zipCode,
                country,
                siteName,
                regionId: region?.id,
                miles,
                minutes,
                toOrFromJob,
            });
            setEffectiveOn(undefined);
            setExpirationDate(undefined);
            setPriceIncreasePercentage(undefined);
            setJobName('');
            setAddressLine1('');
            setAddressLine2('');
            setCity('');
            setState('IN');
            setZipCode('');
            setCountry(countriesByCode.US);
            setSite(undefined);
            setSiteName('');
            setRegion(undefined);
            setMiles(undefined);
            setMinutes(undefined);
            setToOrFromJob('');
        }
    }, [addressLine1, addressLine2, city, country, effectiveOn, expirationDate, jobName, miles, minutes, onConfirm, priceIncreasePercentage, region?.id, siteName, state, toOrFromJob, zipCode]);

    const minEffectiveDate = useMemo(() => {
        if (quoteEffectiveOn) {
            const effectiveOn = toEasternWallDate(quoteEffectiveOn);
            return fromEasternWallDate(new Date(effectiveOn.getFullYear(), effectiveOn.getMonth(), effectiveOn.getDate() + (isCopying ? 0 : 1)));
        }

        return undefined;

    }, [quoteEffectiveOn, isCopying]);

    useEffect(() => {
        if (!effectiveOn) {
            setEffectiveOn(minEffectiveDate);
        }
    }, [effectiveOn, minEffectiveDate]);

    useEffect(() => {
        if (open && minEffectiveDate) {
            setEffectiveOn(minEffectiveDate);
        }
    }, [open, minEffectiveDate]);

    const minExpirationDate = useMemo(() => {
        if (effectiveOn) {
            const day = toEasternWallDate(effectiveOn);
            return fromEasternWallDate(new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1));
        }
        return undefined;
    }, [effectiveOn]);

    const maxExpirationDate = useMemo(() => {
        if (effectiveOn) {
            const year = toEasternWallDate(effectiveOn).getFullYear();
            return fromEasternWallDate(new Date(year, 11, 31, 23, 59, 59, 999));
        }
        return undefined;
    }, [effectiveOn]);

    useEffect(() => {
        if (quoteExpirationDate) {
            setExpirationDate(quoteExpirationDate);
        }
    }, [quoteExpirationDate]);

    useEffect(() => {
        if (minExpirationDate && (!expirationDate || expirationDate < minExpirationDate)) {
            setExpirationDate(minExpirationDate);
        }
    }, [expirationDate, minExpirationDate]);

    const jobFormFields = useMemo(() => {
        return (
            <>
                <Grid item container direction='row' spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                        <AddressLookup
                            handleAddressLine1Change={(value) => setAddressLine1(value)}
                            handleCityChange={(value) => setCity(value)}
                            handleCountryChange={(value) => setCountry(value)}
                            handleStateChange={(value) => setState(value)}
                            handleZipCodeChange={(value) => setZipCode(value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <SiteSelect
                            selectedSiteId={site?.id ?? ''}
                            selectedSiteName={siteName}
                            handleSelectedSiteChange={(value) => {
                                setSite(value);
                                setAddressLine1(value?.addressLine1 ?? '');
                                setAddressLine2(value?.addressLine2 ?? '');
                                setCity(value?.city ?? '');
                                setState(value?.state ?? '');
                                setZipCode(value?.zipCode ?? '');
                                setCountry(value?.country ?? countriesByCode.US);
                                setRegion(value?.region);
                                setSiteName(value?.name ?? '');
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormInput
                            value={siteName}
                            onChange={(e) => setSiteName(e.target.value)}
                            label='Site Name'
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                            <FormLabel>Region</FormLabel>
                            <IEntityAutocomplete
                                options={regions?.pageResults ?? []}
                                onChange={(_e, value) => {
                                    setRegion(value);
                                }}
                                value={region}
                                getOptionLabel={(option: RegionDto) => option.name}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
                <Grid item container direction='row' spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormInput
                            value={addressLine1}
                            onChange={(e) => setAddressLine1(e.target.value)}
                            label='Address Line 1'
                            name='addressLine1'
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormInput
                            value={addressLine2}
                            onChange={(e) => setAddressLine2(e.target.value)}
                            label='Address Line 2'
                            name='addressLine2'
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormInput
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            label='City'
                            name='city'
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <StateAutocomplete
                            fullWidth
                            value={state}
                            onChange={(event, value) => {
                                setState(value ?? "");
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormInput
                            value={zipCode}
                            onChange={(e) => setZipCode(e.target.value)}
                            label='Zip Code'
                            name='zipCode'
                            fullWidth
                        />
                    </Grid>
                </Grid>
                <Grid item container direction='row' spacing={3}>
                    <Grid item xs={3}>
                        <FormControl fullWidth>
                            <FormLabel>Country</FormLabel>
                            <ClearableSelect
                                value={country ?? ''}
                                onChange={(e) => setCountry(e.target.value as string)}
                                onClear={() => setCountry('')}
                            >
                                {countries.map((country) => (
                                    <MenuItem key={country} value={country}>
                                        {country}
                                    </MenuItem>
                                ))}
                            </ClearableSelect>
                        </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                        <FormNumberInput
                            value={miles}
                            name='miles'
                            onChange={(e) => setMiles(e.target.valueAsNumber)}
                            label='Miles'
                            fullWidth
                            inputProps={{ min: 0 }}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <FormNumberInput
                            value={minutes}
                            name='minutes'
                            onChange={(e) => setMinutes(e.target.valueAsNumber)}
                            label='Minutes'
                            fullWidth
                            inputProps={{ min: 0 }}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <FormInput
                            value={toOrFromJob}
                            onChange={(e) => setToOrFromJob(e.target.value)}
                            label='To or from Job'
                            name='toOrFromJob'
                            fullWidth
                        />
                    </Grid>
                </Grid>
            </>
        );
    }, [addressLine1, addressLine2, city, country, miles, minutes, region, regions?.pageResults, site?.id, siteName, state, toOrFromJob, zipCode]);

    return (
        <ConfirmDialog
            maxWidth={isCopying ? 'lg' : 'sm'}
            open={open}
            onClose={() => {
                setEffectiveOn(undefined);
                setPriceIncreasePercentage(undefined);
                onClose();
            }}
            content={
                <Grid item container direction='column' spacing={2}>
                    <Grid item>
                        <Typography>Please enter the following data to initialize the new Quote.</Typography>
                    </Grid>
                    <Grid item container direction='row' spacing={2} justifyContent={!isCopying ? 'space-between' : 'normal'}>
                        <Grid item xs={isCopying ? 3 : 6}>
                            <FormControl fullWidth>
                                <Grid container spacing={2}>
                                    <Grid item>
                                        <FormLabel required>Effective On</FormLabel>
                                    </Grid>
                                    <Grid item>
                                        <Tooltip title='The date the new quote will become effective on.'><Info /></Tooltip>
                                    </Grid>
                                </Grid>
                                <DatePicker
                                    value={effectiveOn ?? minEffectiveDate ?? null}
                                    onChange={(date) => setEffectiveOn(date)}
                                    minDate={minEffectiveDate}
                                    error={!(effectiveOn || minEffectiveDate)}
                                    errorText={!(effectiveOn || minEffectiveDate) ? 'Effective On Date is required.' : ''}
                                />
                            </FormControl>
                        </Grid>
                        {!isCopying && <Grid item xs={6}>
                            <FormControl fullWidth>
                                <Grid container spacing={2}>
                                    <Grid item>
                                        <FormLabel>Price Increase Percentage (%)</FormLabel>
                                    </Grid>
                                    <Grid item>
                                        <Tooltip title='All Line Item rates (except Material Line Items using standard pricing) will be increased using this percentage.'><Info /></Tooltip>
                                    </Grid>
                                </Grid>
                                <OutlinedInput
                                    onChange={
                                        (event: React.ChangeEvent<HTMLInputElement>) => {
                                            setPriceIncreasePercentage(event.target.valueAsNumber);
                                        }
                                    }
                                    type={'number'}
                                    required
                                    placeholder="10"
                                    value={priceIncreasePercentage ?? ''}
                                />
                            </FormControl>
                        </Grid>}
                        {isCopying &&
                            <>
                                <Grid item xs={3} mt={1}>
                                    <Grid container spacing={2}>
                                        <Grid item>
                                            <FormLabel required>Expiration Date</FormLabel>
                                        </Grid>
                                    </Grid>
                                    <DatePicker
                                        value={expirationDate}
                                        onChange={(date) => {
                                            setExpirationDate(date)
                                        }}
                                        minDate={minExpirationDate}
                                        maxDate={maxExpirationDate}
                                        error={!expirationDate}
                                        errorText={!expirationDate ? 'Expiration Date is required.' : ''}
                                    />
                                </Grid>
                                <Grid item xs={3} mt={1}>
                                    <FormInput
                                        value={jobName}
                                        onChange={(event) => setJobName(event.target.value)}
                                        label='Job Name'
                                        fullWidth
                                    />
                                </Grid>
                            </>}
                    </Grid>
                    {isCopying && jobFormFields}
                </Grid>
            }
            title={isCopying ? 'Copy Quote' : 'Clone Quote'}
            titleIcon={<ControlPointDuplicate />}
            onConfirm={handleOnConfirm}
            confirmText='Confirm'
            requireConfirmation={false}
        />
    );
}